import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SbTable from "uiCore/SbTable/SbTable";
import { SkillsBridgerTableWrapperStyled } from "./SkillsBridgerV2.styles";
import useSkillsBridgerColumns from "./useSkillsBridgerColumns";
import SkillsBridgerPaginationTotal from "./SkillsBridgerPaginationTotal";
import { ROUTE } from "./SkillsBridgerV2.constants";

const SbSkillsBridgerHRV2 = ({
  filteredData,
  loading,
  selectedEmployees,
  onSelectionChange,
  onVisibleRowsChange,
}) => {
  const navigate = useNavigate();
  const columns  = useSkillsBridgerColumns();

  // ── Row selection ──────────────────────────────────────────────────────────

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedEmployees.map((e) => e.employee_id),
      onChange: (_, rows) => onSelectionChange(rows),
    }),
    [selectedEmployees, onSelectionChange]
  );

  // ── Pagination ─────────────────────────────────────────────────────────────

  const pagination = useMemo(
    () => ({
      showTotal: (total) => (
        <SkillsBridgerPaginationTotal
          total={total}
          dataLength={filteredData?.length ?? 0}
        />
      ),
    }),
    [filteredData?.length]
  );

  // ── Table change (filter / sort) ───────────────────────────────────────────
  // AntD fires onChange with extra.currentDataSource = all rows that survive
  // the active filters (across all pages). We propagate this upward so the
  // parent knows which employees are currently visible when navigating.

  const handleTableChange = useCallback(
    (_pagination, _filters, _sorter, extra) => {
      onVisibleRowsChange(extra.currentDataSource);
    },
    [onVisibleRowsChange]
  );

  // ── Row handler ────────────────────────────────────────────────────────────
  // Cursor is controlled via .skills-bridger-row in SkillsBridgerV2.styles.js.

  const onRow = useCallback(
    (record) => ({
      title: "View Employee Skills",
      onClick: (e) => {
        if (e.target.closest("td")?.classList.contains("ant-table-selection-column")) return;
        navigate(ROUTE.EMPLOYEE_SKILLS_MAP, { state: { employee: record } });
      },
    }),
    [navigate]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SkillsBridgerTableWrapperStyled vertical>
      <SbTable
        columns={columns}
        dataSource={filteredData}
        rowKey="employee_id"
        rowSelection={rowSelection}
        loading={loading}
        sticky
        rowClassName="skills-bridger-row"
        pagination={pagination}
        onRow={onRow}
        onChange={handleTableChange}
      />
    </SkillsBridgerTableWrapperStyled>
  );
};

export default SbSkillsBridgerHRV2;
